import axios from 'axios';

// Create an axios instance with the correct base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL, // should resolve to https://devexam.doode.in
  timeout: 10000,
  withCredentials: true // Important for CORS with credentials
});

// Add request interceptor with improved logging for debugging
api.interceptors.request.use(
  config => {
    console.log(`Making ${config.method.toUpperCase()} request to ${config.baseURL}${config.url}`);
    try {
      const fullUrl = new URL(config.url, config.baseURL).toString();
      console.log(`Full request URL: ${fullUrl}`);
    } catch (e) {
      console.log(`Could not construct full URL: ${e.message}`);
    }
    return config;
  },
  error => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for better error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response) {
      console.error('Server error:', error.response.status, error.response.data);
    } else if (error.request) {
      console.error('Network error - no response received');
      console.error('Request details:', JSON.stringify({
        url: error.config?.url,
        baseURL: error.config?.baseURL,
        method: error.config?.method
      }));
    } else {
      console.error('Request configuration error:', error.message);
    }
    return Promise.reject(error);
  }
);

// Question API calls
export const questionAPI = {
  getAll: () => api.get('/api/questions'),
  getById: (id) => api.get(`/api/questions/${id}`),
  create: (data) => api.post('/api/questions', data),
  update: (id, data) => api.put(`/api/questions/${id}`, data),
  delete: (id) => api.delete(`/api/questions/${id}`),
  createBulk: (data) => api.post('/api/questions/bulk', data)
};

// Student API calls
export const studentAPI = {
  getAll: () => api.get('/api/students'),
  getById: (id) => api.get(`/api/students/${id}`),
  create: (data) => api.post('/api/students', data),
  
  // Register a student through the admin interface
  // FIXED: Use the correct admin/register endpoint for admin-created students
  register: (studentData) => {
    console.log('Registering student via admin:', studentData);
    return api.post('/api/student/admin/register', studentData);
  },
  
  // Register a student through the portal (separate function for clarity)
  portalRegister: (studentData) => {
    console.log('Registering student via portal:', studentData);
    return api.post('/api/student/register', studentData);
  },
  
  update: (id, data) => api.put(`/api/students/${id}`, data),
  delete: (id) => api.delete(`/api/students/${id}`),
  
  // Bulk create students with admin privileges
  // FIXED: Use the correct admin/bulk endpoint for bulk student creation
  createBulk: (data) => {
    console.log('Bulk creating students:', data);
    return api.post('/api/student/admin/bulk', data);
  },
  
  // Added new stats methods
  stats: {
    getTotal: () => api.get('/api/student-stats/total')
  }
};

// Assignment API calls
export const assignmentAPI = {
  getAll: () => api.get('/api/assignments'),
  getById: (id) => api.get(`/api/assignments/${id}`),
  create: (data) => api.post('/api/assignments', data),
  createBatch: (data) => api.post('/api/assignments/batch', data),
  complete: (id, data) => api.put(`/api/assignments/${id}/complete`, data),
  getByStudent: (studentId) => api.get(`/api/assignments/student/${studentId}`)
};

// Enhanced health check to provide more diagnostic information
export const healthCheck = () => api.get('/api/health')
  .then(response => ({
    status: 'connected',
    details: response.data,
    baseUrl: api.defaults.baseURL
  }))
  .catch(error => ({
    status: 'error',
    message: error.message,
    baseUrl: api.defaults.baseURL
  }));

export default api;